/**
 * 自动预算（Auto Budget）· **契约层**（2026-10-07 · Lead 授权新建，一期只读预演）。
 *
 * 口径唯一来源：`docs/plan/auto-budget-design.md` §1–§7。本文件**零依赖纯函数 + 常量**
 * （AGENTS.md §2：契约层不得有 DB/HTTP/env 依赖）。
 *
 * 三条硬性质（都是"设计事故防复发"）：
 *  1. **判别联合**：`hold` / 跳过分支**类型上不带 `newBudgetCents`** ⇒ 不可能发生
 *     「给不动作的行做 clamp/round ⇒ 造出假变化」（§6 那次 −$93,417 的根因）；
 *  2. **中位数基线**（不是花费加权均值）：按**店铺**算层的 7 天 ROAS 中位数，样本量
 *     `n ≥ minSamples`（默认 5）否则**不出基线**（该店铺的层一律跳过，见 §7 实测：
 *     加权均值方案偏低占比 62.4%/中位 0.838；中位数方案 49.1%/中位 1.000）；
 *  3. **比值/倍数一律 ppm 整数**（`1_000_000 = 100%`）：避免浮点误差与"边界靠运气"。
 */

import type { Cents } from "./metrics.js";

/** 比例统一用 ppm（百万分之一）整数表示：`1_000_000` = 100%。 */
export type Ppm = number;

/** 归因口径的 7 天 ROAS（无量纲；缺失 ⇒ `null`，**绝不折 0**）。 */
export type Roas7d = number;

// ---------------------------------------------------------------------------
// 档位表
// ---------------------------------------------------------------------------

export type AutoBudgetTierAction =
  | { readonly kind: "fixed"; readonly cents: number }
  | { readonly kind: "multiply"; readonly factorPpm: Ppm }
  | { readonly kind: "hold" };

/**
 * ⭐⭐ **档位文案 = 区间 + 该档自己的动作**（例：`高于 180%（日预算 120%）`）。
 *
 * ⛔ **不要拿 `tier.label` 当展示文案**：那是"内置默认阶梯"的**示例文案**，
 * 用户在模板里改过系数之后它就变成**旧话** —— 2026-10-09 实测事故：
 * 模板顶层系数改成 `120%`，预演仍显示「高于 180%（日预算 130%）」，而新预算按 120% 算
 * （$350 → $420）⇒ **显示与执行不同源**（本仓库红线）。
 *
 * 本函数**只从 `minRatioPpm` / `maxRatioPpm` / `action` 派生** ⇒ 与判定读的是**同一个对象**，
 * 物理同源；`label` 降级为"默认阶梯的说明文字"，不再参与任何展示。
 */
export function autoBudgetTierText(tier: AutoBudgetRoasTier): string {
  const percent = (ppm: number): string => {
    const value = ppm / 10_000;
    return `${Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)))}%`;
  };
  const range =
    tier.minRatioPpm === null
      ? `低于 ${percent(tier.maxRatioPpm ?? 0)}`
      : tier.maxRatioPpm === null
        ? `高于 ${percent(tier.minRatioPpm)}`
        : `${percent(tier.minRatioPpm)} – ${percent(tier.maxRatioPpm)}`;
  const action =
    tier.action.kind === "hold"
      ? "不变：死区"
      : tier.action.kind === "fixed"
        ? `固定 $${(tier.action.cents / 100).toFixed(2)}`
        : `日预算 ${percent(tier.action.factorPpm)}`;
  return `${range}（${action}）`;
}

/**
 * ⭐ **生效预算带** = `[max(模板预算MINI, 账户币种最低日预算), 模板预算MAX]`。
 *
 * 判定里本来就内联了这条 `floor`；抽出来给**展示侧**（预演的"新预算"标红、绑定时提示）
 * 复用 —— ⛔ 不许各算一份（"显示与执行必须同源"）。
 */
export function effectiveBudgetBand(
  template: Pick<AutoBudgetTemplateConfig, "budgetMinCents" | "budgetMaxCents">,
  currencyMinDailyBudgetCents: number | null,
): { readonly minCents: number; readonly maxCents: number } {
  return {
    minCents: Math.max(template.budgetMinCents, currencyMinDailyBudgetCents ?? 0),
    maxCents: template.budgetMaxCents,
  };
}

export interface AutoBudgetRoasTier {
  readonly id: string;
  /** 下界（**闭**）；`null` = 无下界（最低档）。 */
  readonly minRatioPpm: Ppm | null;
  /** 上界（**开**）；`null` = 无上界（最高档）。 */
  readonly maxRatioPpm: Ppm | null;
  readonly action: AutoBudgetTierAction;
  readonly label: string;
}

/**
 * ⭐⭐ **内置默认阶梯 = 用户 2026-10-09 的表格（11 档，逐档核对过）**。
 *
 * 比值 = **该预算层的 ROAS7天 ÷ 店铺 ROAS7天**（两者同为 `meta_attributed` / `TOTAL`、同窗口）。
 * 区间 = `[min, max)`（**下界闭、上界开**）；首档 `min = null`、末档 `max = null`。
 *
 * ⚠️ **历史**：此前是照截图做的 **10 档、切点从未核对**（自注"待与模板截图核对"）。
 * 2026-10-09 按用户表格修正 **6 处 + 补 1 档**：删 85% 切点 ⇒ `70–80 / 80–90 / 90–100`
 *（**补回缺失的 80–90 档**）；`130–150` 倍数 **115% ⇒ 120%**；`150–170 / ≥170`
 * ⇒ **`150–180 / >180`**。反向守卫（旧值复活即红）见 `auto-budget.spec.ts`。
 *
 * ⚠️ `fixed.cents` 必须落在 `[budgetMinCents, budgetMaxCents]`（校验器强制）⇒ 改 MIN 要同步改该档。
 */
export const AUTO_BUDGET_ROAS_TIERS: readonly AutoBudgetRoasTier[] = [
  { id: "lt50", minRatioPpm: null, maxRatioPpm: 500_000, action: { kind: "fixed", cents: 500 }, label: "低于 50%（固定 $5 = 预算MINI）" },
  { id: "50_60", minRatioPpm: 500_000, maxRatioPpm: 600_000, action: { kind: "multiply", factorPpm: 600_000 }, label: "50–60%（日预算 60%）" },
  { id: "60_70", minRatioPpm: 600_000, maxRatioPpm: 700_000, action: { kind: "multiply", factorPpm: 700_000 }, label: "60–70%（日预算 70%）" },
  { id: "70_80", minRatioPpm: 700_000, maxRatioPpm: 800_000, action: { kind: "multiply", factorPpm: 800_000 }, label: "70–80%（日预算 80%）" },
  { id: "80_90", minRatioPpm: 800_000, maxRatioPpm: 900_000, action: { kind: "multiply", factorPpm: 900_000 }, label: "80–90%（日预算 90%）" },
  { id: "90_100", minRatioPpm: 900_000, maxRatioPpm: 1_000_000, action: { kind: "multiply", factorPpm: 950_000 }, label: "90–100%（日预算 95%）" },
  { id: "100_115", minRatioPpm: 1_000_000, maxRatioPpm: 1_150_000, action: { kind: "hold" }, label: "100–115%（不变）" },
  { id: "115_130", minRatioPpm: 1_150_000, maxRatioPpm: 1_300_000, action: { kind: "multiply", factorPpm: 1_100_000 }, label: "115–130%（日预算 110%）" },
  { id: "130_150", minRatioPpm: 1_300_000, maxRatioPpm: 1_500_000, action: { kind: "multiply", factorPpm: 1_200_000 }, label: "130–150%（日预算 120%）" },
  { id: "150_180", minRatioPpm: 1_500_000, maxRatioPpm: 1_800_000, action: { kind: "multiply", factorPpm: 1_250_000 }, label: "150–180%（日预算 125%）" },
  { id: "gt180", minRatioPpm: 1_800_000, maxRatioPpm: null, action: { kind: "multiply", factorPpm: 1_300_000 }, label: "高于 180%（日预算 130%）" },
];

/** 模板配置版本：`2` = 新口径；`1` = **legacy**（旧形状 ⇒ 判定 fail closed）。 */
export const AUTO_BUDGET_CONFIG_VERSION = 2;
export const AUTO_BUDGET_LEGACY_CONFIG_VERSION = 1;
/** `multiply.factorPpm` 合理上界（`0` 拒绝：防"乘 0 写 0"）。 */
export const AUTO_BUDGET_MAX_FACTOR_PPM = 2_000_000;

/**
 * 按 ppm 比值取档（**下界闭、上界开**）。
 * `tiers` 缺省 = 内置默认表；**模板级**调用必须显式传 `template.tiers`。
 */
export function roasTierOf(
  ratioPpm: Ppm,
  tiers: readonly AutoBudgetRoasTier[] = AUTO_BUDGET_ROAS_TIERS,
): AutoBudgetRoasTier {
  for (const tier of tiers) {
    const aboveMin = tier.minRatioPpm === null || ratioPpm >= tier.minRatioPpm;
    const belowMax = tier.maxRatioPpm === null || ratioPpm < tier.maxRatioPpm;
    if (aboveMin && belowMax) return tier;
  }
  const last = tiers[tiers.length - 1];
  const first = tiers[0];
  if (last === undefined || first === undefined) throw new Error("档位表为空");
  return ratioPpm < (first.maxRatioPpm ?? 0) ? first : last;
}

// ---------------------------------------------------------------------------
// 跳过原因（§4.9 六条 + §4.4 迟滞两条 + 不变区）
// ---------------------------------------------------------------------------

export type AutoBudgetSkipReason =
  | "spend_below_min_30d"
  | "interval_not_elapsed"
  | "already_at_min"
  | "already_at_max"
  | "no_budget_layer"
  | "hold_band"
  | "template_min_invalid"
  | "template_legacy_config"
  | "layer_roas_missing"
  /**
   * ⭐ 2026-10-10（用户报障）：**有花费但归因收入为 0** ⇒ `ROAS = 0`。
   *
   * 这是**真值不是缺失**（`null` 才表示"窗口内没有花费 ⇒ 分母 0 ⇒ 无法计算"）——
   * 两者在 store 侧天然可分：`revenue7d / spend7d` 的"分母 0 ⇒ null"约定意味着
   * `null ⟺ spend7d = 0`、`0 ⟺ spend7d > 0 ∧ revenue7d = 0`。
   *
   * ⚠️ 本条**当前行为**：不动作（`hold`）。是否改为"走模板最低档（降预算）"由 Lead 裁定
   * （会动真钱 + 涉及归因延迟），裁定前**保持不动作但如实说明**，⛔ 不许再说成"取不到"。
   */
  | "layer_roas_zero"
  | "shop_roas_missing";

/** 每条带**中文 message**：直接进预演/流水的「判定/说明」列（不静默）。 */
export const AUTO_BUDGET_SKIP_REASONS: Readonly<Record<AutoBudgetSkipReason, string>> = {
  spend_below_min_30d: "30 天花费未达门槛：该预算层不启动自动预算",
  interval_not_elapsed: "未到调整间隔：同一预算层最多 N 天调整 1 次（N = 模板的 maxAdjustIntervalDays）",
  already_at_min: "已在 MIN：不再下调",
  already_at_max: "已在 MAX：不再上调",
  no_budget_layer: "该系列没有可评估的预算层（缺当前日预算）",
  hold_band: "比值落在不变区（模板阶梯里的 hold 档，如 100–115%）",
  template_min_invalid: "模板的 MIN/MAX 非法（如 MIN = 0）：拒绝动作（fail closed）",
  template_legacy_config:
    "该模板是**旧口径**（未按新阶梯重新保存）⇒ 本次不调整（⛔ 不按旧字段凑、也不硬套新阶梯）",
  layer_roas_missing: "该预算层 7 天内没有花费（分母 0）⇒ 无法计算 ROAS ⇒ 本次不调整（缺失 ≠ 0）",
  layer_roas_zero:
    "该预算层 7 天有花费但归因收入为 0 ⇒ 视为**疑似未抓到（归因缺失）**，按最低档处理（降预算）；若此处仍出现，说明被其它闸门挡住（如未到间隔 / 已在 MIN）",
  shop_roas_missing: "店铺 ROAS 取不到（7 天无花费或跨币种），不是 0 ⇒ 本次不调整",
};

// ---------------------------------------------------------------------------
// ⭐ 2026-10-12 用户裁定（截图报障）：`有花费但归因收入为 0`（`layerRoas7d === 0`）
//     ⇒ 视为「**疑似未抓到**（归因缺失）」，不再当"真实 0 ⇒ 不动作"✗。
//
//   原口径（2026-10-10）：`0` 是**真值不是缺失** ⇒ 不动作（注释里写明"是否改走最低档待 Lead 裁定"）。
//   用户原话：「真实的原因是 ROAS 是 0，但是系统现在认为 0，就是没抓到；
//              要改成**抓不到、达到了花费底线，就要调整了**」。
//
//   ⇒ 新行为：把 ratio 视作 **0**（= 理论上最差）继续走后面的闸门 ⇒ 命中**最低档** ⇒ **降预算** ✓；
//      ⛔ 所有护栏一条不放宽：30 天花费门槛 ✓ / 间隔 ✓ / MIN 夹取 ✓ / 死区 ✓ / 审计 ✓。
//      ⚠️ 与"真缺失"（`null` = 窗口内**没有花费** ⇒ 分母 0）仍分开：那条继续 `layer_roas_missing` ✓。
//
//   🔙 一键回退：把下面常量改成 `"hold"` ⇒ 立即恢复 2026-10-10 的旧口径（不动作）✓。
export const AUTO_BUDGET_ZERO_ROAS_ACTION: "treat_as_lowest" | "hold" = "treat_as_lowest";

// ---------------------------------------------------------------------------
// 模板参数（MIN/MAX 是**每个模板自己的参数**；币种由系列账户决定，不进配置）
// ---------------------------------------------------------------------------

export interface AutoBudgetTemplateConfig {
  /** `2` = 新口径；`1` = legacy（旧形状，判定 fail closed）。 */
  readonly configVersion: number;
  /** **预算MINI**（分）。 */
  readonly budgetMinCents: number;
  /** **预算MAX**（分）。 */
  readonly budgetMaxCents: number;
  /** **最小 30 天花费**（分）：低于它该层不启动自动预算。 */
  readonly minSpend30dCents: number;
  /** 同一预算层**最多几天调整 1 次**（用户口径 = 2）。 */
  readonly maxAdjustIntervalDays: number;
  /**
   * ⭐⭐ 2026-10-11 用户裁定：「单步 10% 要在**面板上能调**」⇒ **模板级**爬坡步长（ppm）。
   *  · 缺省（未填）⇒ 回落环境变量 `AMS_TARGET_RAMP_STEP_PPM` ⇒ 再回落 10%（`100_000`）；
   *  · `0` = **该模板退出爬坡**（历史口径，保留 ✓）；
   *  · 合法区间 `[10_000, 500_000]`（1%–50%）；非法值由 `validateAutoBudgetTemplateConfig` 以人话拒绝 ✓，
   *    解析侧（`resolveTargetRampStepPpm`）对非法值**回落下一优先级**（⛔ 不抛异常、不静默变 0 ✗）。
   * ⚠️ 解析一律走 `resolveTargetRampStepPpm(...)`（单一来源 ✓），⛔ 不许各包自己写一套。
   */
  readonly targetStepPpm?: number;
  /** ⭐ 分档阶梯：**有序、覆盖全区间、无洞无重叠**；区间 `[min, max)`。 */
  readonly tiers: readonly AutoBudgetRoasTier[];
}

export interface AutoBudgetTemplateRejection {
  readonly field: string;
  readonly code: string;
  /** 人话（后台/UI 直接显示，不吞成"保存失败"）。 */
  readonly message: string;
}

export interface AutoBudgetTemplateValidation {
  readonly ok: boolean;
  readonly rejections: readonly AutoBudgetTemplateRejection[];
}

/**
 * 校验模板配置（**创建/更新模板的入口必须调用**）。
 *
 * ⭐ 2026-10-09 新口径：`{configVersion, budgetMinCents, budgetMaxCents, minSpend30dCents,
 * maxAdjustIntervalDays, tiers}`。逐条 rejection，**⛔ 不静默修正**：
 *  · `configVersion === 2`；`budgetMinCents > 0`；`budgetMaxCents >= budgetMinCents`；
 *    `minSpend30dCents >= 0`；`maxAdjustIntervalDays ≥ 1`（整数）；
 *  · `tiers`：非空、`id` 唯一非空、**首档 `min=null` / 末档 `max=null`**、
 *    **相邻档 `prev.max === next.min`（逐字相等，⛔ 无容差）**、每档区间非空；
 *  · `fixed.cents ∈ [budgetMinCents, budgetMaxCents]`（⛔ 不 clamp 抹平 ⇒ 直接 400）；
 *  · `multiply.factorPpm ∈ (0, AUTO_BUDGET_MAX_FACTOR_PPM]`（`0` 拒绝：防"乘 0 写 0"）。
 */
export function validateAutoBudgetTemplateConfig(config: AutoBudgetTemplateConfig): AutoBudgetTemplateValidation {
  const rejections: AutoBudgetTemplateRejection[] = [];
  const reject = (field: string, code: string, message: string): void => {
    rejections.push({ field, code, message });
  };
  const safeInt = (field: string, value: number): boolean => {
    if (!Number.isSafeInteger(value)) {
      reject(field, "not_integer", `${field} 必须是整数（分/ppm），实际：${String(value)}`);
      return false;
    }
    return true;
  };

  if (config.configVersion !== AUTO_BUDGET_CONFIG_VERSION) {
    reject(
      "configVersion",
      "legacy_config",
      `configVersion 必须是 ${String(AUTO_BUDGET_CONFIG_VERSION)}（旧口径模板请按新阶梯重新保存）`,
    );
  }
  if (safeInt("budgetMinCents", config.budgetMinCents) && config.budgetMinCents <= 0) {
    reject("budgetMinCents", "min_not_positive", "预算MINI 必须大于 0（0 会把预算写成关停）");
  }
  if (safeInt("budgetMaxCents", config.budgetMaxCents)) {
    if (config.budgetMaxCents <= 0) reject("budgetMaxCents", "max_not_positive", "预算MAX 必须大于 0");
    if (config.budgetMaxCents < config.budgetMinCents) {
      reject("budgetMaxCents", "max_below_min", "预算MAX 不能小于预算MINI");
    }
  }
  if (safeInt("minSpend30dCents", config.minSpend30dCents) && config.minSpend30dCents < 0) {
    reject("minSpend30dCents", "negative", "30 天花费门槛不能为负");
  }
  if (!Number.isSafeInteger(config.maxAdjustIntervalDays) || config.maxAdjustIntervalDays < 1) {
    reject("maxAdjustIntervalDays", "invalid_interval", "调整间隔必须是不小于 1 的整数（天）");
  }
  // ⭐⭐ 模板级爬坡步长（可选）：`0` = 该模板退出爬坡（合法 ✓）；其余必须是 1%–50% 的整数 ppm。
  if (config.targetStepPpm !== undefined) {
    const step = config.targetStepPpm;
    if (!Number.isSafeInteger(step) || step < 0) {
      reject("targetStepPpm", "invalid_step", "爬坡单步幅度必须是非负整数 ppm（0 = 该模板退出爬坡）");
    } else if (step !== 0 && (step < TARGET_RAMP_STEP_PPM_MIN || step > TARGET_RAMP_STEP_PPM_MAX)) {
      reject(
        "targetStepPpm",
        "step_out_of_range",
        `爬坡单步幅度需在 1%–50% 之间（0 = 退出爬坡），当前 ${String(step / 10_000)}%`,
      );
    }
  }

  const tiers = config.tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) {
    reject("tiers", "empty_tiers", "分档阶梯不能为空（旧口径模板请按新阶梯重新保存）");
    return { ok: rejections.length === 0, rejections };
  }
  const seenIds = new Set<string>();
  tiers.forEach((tier, index) => {
    const at = `tiers[${String(index)}]`;
    if (typeof tier.id !== "string" || tier.id.trim() === "") {
      reject(`${at}.id`, "invalid_id", "档位 id 必须是非空字符串");
    } else if (seenIds.has(tier.id)) {
      reject(`${at}.id`, "duplicate_id", `档位 id 重复：${tier.id}`);
    } else {
      seenIds.add(tier.id);
    }
    const min = tier.minRatioPpm;
    const max = tier.maxRatioPpm;
    if (min !== null && (!Number.isSafeInteger(min) || min < 0)) {
      reject(`${at}.minRatioPpm`, "invalid_ppm", "下界必须是非负整数 ppm 或 null");
    }
    if (max !== null && (!Number.isSafeInteger(max) || max < 0)) {
      reject(`${at}.maxRatioPpm`, "invalid_ppm", "上界必须是非负整数 ppm 或 null");
    }
    if (min !== null && max !== null && min >= max) {
      reject(`${at}`, "empty_interval", `档位区间为空：[${String(min)}, ${String(max)})`);
    }
    if (index === 0 && min !== null) {
      reject(`${at}.minRatioPpm`, "first_tier_bounded", "首档下界必须是 null（覆盖 −∞）");
    }
    if (index === tiers.length - 1 && max !== null) {
      reject(`${at}.maxRatioPpm`, "last_tier_bounded", "末档上界必须是 null（覆盖 +∞）");
    }
    const previous = tiers[index - 1];
    if (previous !== undefined && previous.maxRatioPpm !== min) {
      reject(
        `${at}.minRatioPpm`,
        "gap_or_overlap",
        `与上一档不相接（上一档上界 ${String(previous.maxRatioPpm)}，本档下界 ${String(min)}）⇒ 有洞或重叠`,
      );
    }
    const action = tier.action;
    if (action.kind === "fixed") {
      if (!Number.isSafeInteger(action.cents)) {
        reject(`${at}.action.cents`, "not_integer", "固定值必须是整数分");
      } else if (action.cents < config.budgetMinCents || action.cents > config.budgetMaxCents) {
        reject(
          `${at}.action.cents`,
          "fixed_out_of_range",
          `固定值 ${String(action.cents)} 必须在 [预算MINI, 预算MAX] 内（不 clamp 抹平）`,
        );
      }
    } else if (action.kind === "multiply") {
      if (
        !Number.isSafeInteger(action.factorPpm) ||
        action.factorPpm <= 0 ||
        action.factorPpm > AUTO_BUDGET_MAX_FACTOR_PPM
      ) {
        reject(
          `${at}.action.factorPpm`,
          "invalid_factor",
          `倍数必须在 (0, ${String(AUTO_BUDGET_MAX_FACTOR_PPM)}] ppm 内（0 会写出 0 预算）`,
        );
      }
    } else if (action.kind !== "hold") {
      reject(`${at}.action`, "invalid_action", "档位动作必须是 fixed / multiply / hold");
    }
  });

  return { ok: rejections.length === 0, rejections };
}

// ---------------------------------------------------------------------------
// 判定：判别联合（hold 分支**类型上不带** newBudgetCents）
// ---------------------------------------------------------------------------

export type AutoBudgetDirection = "increase" | "decrease";

export interface AutoBudgetHoldDecision {
  readonly outcome: "hold";
  /** `null` = 落在"不变区"（无跳过原因，但仍**不产生 `newBudgetCents`**）。 */
  readonly skipReason: AutoBudgetSkipReason | null;
  /** 仅供可解释性（如比值算得出来就带上）。 */
  readonly ratioPpm: Ppm | null;
  /**
   * ⭐ 2026-10-15 用户裁定（原话要点）：「自动预算，**不要写没有到间隔时间**，应该写**计划调整的金额是多少**，
   * 然后**自动爬坡要乘以多少**。例如：当前预算 1200 × 1.1（自动预算）× 1.1（自动爬坡）= XXXX，
   * **下次调整时间是 XXXX**」。
   *
   * 语义：**只算不写的"计划值"** —— 若间隔已经过了，本次**会写成的**预算
   * （= 按档位系数算出的目标，已夹到 `[生效下限, 模板 budgetMaxCents]`，与真动作分支**同一个 helper** ✓）。
   *
   * ⛔ 三条硬约束：
   *  1. **只有 `skipReason === "interval_not_elapsed"` 且档位确实会动作时**才给值（其余 hold ⇒ `null`）——
   *     因为只有"差时间"这件事会变；档位本来就不动（死区/`already_at_max`/花费未达门槛）的话，
   *     计划值 = 什么都不做 ⇒ 给一个数会**误导**成"将会调整" ✗；
   *  2. 它**绝不**代表真的会写（间隔没到就是不写 ✓）—— 类型上 `hold` 分支**没有** `newBudgetCents`，
   *     写通道在结构上取不到它 ✓；
   *  3. `outcome` 仍是 `"hold"` ⇒ 既有调用方（执行趟/方向门）行为**零变化** ✓。
   */
  readonly plannedNewBudgetCents?: number | null | undefined;
  /** 计划用的**档位系数**（ppm；可解释性 ⇒ 面板能显示「× 1.10」而不是一个凭空的金额）。`fixed` 档 ⇒ `null`。 */
  readonly plannedFactorPpm?: Ppm | null | undefined;
  /** 计划用的**档位 id**（与真动作分支同源，便于排障）。 */
  readonly plannedTierId?: string | null | undefined;
}

export interface AutoBudgetActionDecision {
  readonly outcome: AutoBudgetDirection;
  /** **只有确实会动作的行**才有这个字段**（clamp/round 只在这里发生）。 */
  readonly newBudgetCents: number;
  readonly ratioPpm: Ppm;
  readonly tierId: string;
  /** `true` ⇒ 结果被 MIN/MAX 或币种最低日预算**夹过**（可解释性）。 */
  readonly clamped: boolean;
}

export type AutoBudgetDecision = AutoBudgetHoldDecision | AutoBudgetActionDecision;

/** 币种最低日预算查询：**未知币种 ⇒ `null`（无下限、如实标注）**，绝不编数字。 */
export function metaMinDailyBudgetCents(currency: string): number | null {
  return META_MIN_DAILY_BUDGET_CENTS_BY_CURRENCY[currency.toUpperCase()] ?? null;
}

/**
 * Meta 各币种最低日预算（分）。
 *
 * ⚠️ **默认空表**：只收录**有据可查**的项；缺项 ⇒ `metaMinDailyBudgetCents` 返回 `null`，
 * 调用方按"**无下限**"处理并**在输出里如实标注**（§4.3：别编数字）。
 */
export const META_MIN_DAILY_BUDGET_CENTS_BY_CURRENCY: Readonly<Record<string, number>> = {};

/** 判定入参（全部由调用方从读侧取好；本函数不查库）。 */
export interface EvaluateAutoBudgetInput {
  readonly currentBudgetCents: number;
  readonly spend30dCents: number;
  readonly layerRoas7d: Roas7d | null;
  /**
   * ⭐ **店铺 ROAS 7 天**（分母）：`meta_attributed` / `TOTAL` / `level='account'` 按店铺合计，
   * 与**分子同一条事实链、同一窗口**（2026-10-09 用户裁定；**不再是**"层 ROAS 中位数基线"）。
   * `null` = 取不到（窗口内无事实行 / 跨币种）⇒ **不动作**（`shop_roas_missing`）。
   */
  readonly shopRoas7d: Roas7d | null;
  readonly template: AutoBudgetTemplateConfig;
  /** 上一次调整时间（ISO）；`null` = 从未调整。 */
  readonly lastAdjustedAtIso: string | null;
  /** 当前时刻（ISO，注入以便测试确定性）。 */
  readonly nowIso: string;
  /** 该系列账户币种的 Meta 最低日预算（`null` = 无下限）。 */
  readonly currencyMinDailyBudgetCents: number | null;
}

/** 调整间隔下限（§2：同一预算层最多 2 天调整 1 次）。 */
export const AUTO_BUDGET_MIN_INTERVAL_DAYS = 2;

function hold(skipReason: AutoBudgetSkipReason | null, ratioPpm: Ppm | null): AutoBudgetHoldDecision {
  return { outcome: "hold", skipReason, ratioPpm };
}

/**
 * **核心判定**（纯函数，2026-10-09 用户 11 档口径）：算"这一层这次该不该动、动到多少"。
 *
 * 判定顺序（**配置/口径优先，先跳过后动作**；任一不过 ⇒ `hold`，**类型上不带 `newBudgetCents`**）：
 *  1. **legacy 模板**（`configVersion ≠ 2` / 空 `tiers`）⇒ `template_legacy_config`；
 *  2. **MIN/MAX 非法**（`MIN ≤ 0` / `MAX < 生效下限`）⇒ `template_min_invalid`；
 *  3. 分子（**该预算层 ROAS7天**）缺失/非正 ⇒ `layer_roas_missing`；
 *  4. 分母（**店铺 ROAS7天**，与分子**同归因口径、同窗口**）缺失/0/负 ⇒ `shop_roas_missing`；
 *  5. `spend30dCents < minSpend30dCents` ⇒ `spend_below_min_30d`；
 *  6. 距**上次成功调整**不足 `maxAdjustIntervalDays` 天 ⇒ `interval_not_elapsed`；
 *  7. 取档（**下界闭、上界开**）：`hold` 档（如 100–115%）⇒ `hold_band`；
 *  8. `fixed` ⇒ 目标 = `cents`；`multiply` ⇒ `round(当前 × factorPpm / 1e6)`；再夹到
 *     `[max(budgetMinCents, 账户币种最低日预算), budgetMaxCents]`；
 *  9. 夹完 == 当前 ⇒ 不动作（边界给 `already_at_min` / `already_at_max`，否则 `skipReason=null`）。
 */
export function evaluateAutoBudgetDecision(input: EvaluateAutoBudgetInput): AutoBudgetDecision {
  const template = input.template;

  // ① **口径与配置优先于一切业务结果**：
  //    legacy（旧口径模板）⇒ fail closed：⛔ 不拿旧字段凑、⛔ 不硬套新阶梯。
  if (template.configVersion !== AUTO_BUDGET_CONFIG_VERSION || template.tiers.length === 0) {
    return hold("template_legacy_config", null);
  }
  //    MIN 非法（`<= 0`）或 `MAX < 生效下限` ⇒ 先于"不变区/门槛/间隔"暴露（否则运维永远不修坏模板；
  //    MIN=0 时 `fixed` 档会把日预算写成 0）。
  const floor = effectiveBudgetBand(template, input.currencyMinDailyBudgetCents ?? null).minCents;
  if (floor <= 0 || template.budgetMaxCents < floor) return hold("template_min_invalid", null);

  // ② 分子（该预算层 7 天 ROAS）：**缺失 ≠ 0**，两者必须分开说（2026-10-10 用户报障）。
  //    · `null` = 该层 7 天**没有花费**（store 的"分母 0 ⇒ null"约定）⇒ 无法计算 ⇒ 不动作；
  //    · `0` = 该层 7 天**有花费**但归因收入为 0 ⇒ 真值 0（不是缺失）⇒ 当前口径仍不动作，
  //      但**文案必须如实**；是否改走最低档（降预算）待 Lead 裁定。
  const layerRoas = input.layerRoas7d;
  if (layerRoas === null) return hold("layer_roas_missing", null);
  if (!Number.isFinite(layerRoas)) return hold("layer_roas_missing", null);
  // ⭐ 2026-10-12 用户裁定：`0` = **有花费但归因收入为 0** ⇒ 视为「疑似未抓到」✓
  //    ⇒ 不再 hold ✗；改为 ratio 视作 0 继续（命中最低档 ⇒ 降预算 ✓）。
  //    `hold` 分支保留在开关里（一键回退 ✓）。
  let zeroRoasNotCaptured = false;
  if (layerRoas <= 0) {
    if (AUTO_BUDGET_ZERO_ROAS_ACTION === "hold") return hold("layer_roas_zero", null);
    zeroRoasNotCaptured = true;
  }
  // ③ 分母（**店铺 ROAS 7 天**；与分子**同一归因口径** `meta_attributed`/`TOTAL`、**同一窗口**）：
  //    缺失 / 0 / 负 ⇒ `shop_roas_missing`（⛔ 不折 0、⛔ 不用替代值）。
  const shopRoas = input.shopRoas7d;
  if (shopRoas === null || !Number.isFinite(shopRoas) || shopRoas <= 0) {
    return hold("shop_roas_missing", null);
  }

  // ⭐ 疑似未抓到 ⇒ ratio 视作 0（最差）⇒ 后面自然命中最低档 ✓
  const ratioPpm = zeroRoasNotCaptured ? 0 : Math.round((layerRoas / shopRoas) * 1_000_000);

  // ④ 30 天花费门槛。
  if (input.spend30dCents < template.minSpend30dCents) return hold("spend_below_min_30d", ratioPpm);

  /**
   * ⭐ 档位目标（**唯一实现**：真动作分支与"计划值"共用 ⇒ ⛔ 不许复制第二份 clamp 逻辑 ✗）。
   * 返回 `null` = 该档位**不动作**（`hold` 档，如 100–115%）。
   */
  const clampedTargetOf = (
    tier: AutoBudgetRoasTier,
  ): { readonly cents: number; readonly clamped: boolean; readonly factorPpm: Ppm | null } | null => {
    if (tier.action.kind === "hold") return null;
    const rawTarget =
      tier.action.kind === "fixed"
        ? tier.action.cents
        : Math.round((input.currentBudgetCents * tier.action.factorPpm) / 1_000_000);
    const clampedTarget = Math.min(Math.max(rawTarget, floor), template.budgetMaxCents);
    return {
      cents: clampedTarget,
      clamped: clampedTarget !== rawTarget,
      factorPpm: tier.action.kind === "fixed" ? null : tier.action.factorPpm,
    };
  };

  // ⑤ 间隔：`now − 上次【成功调整】时刻 >= maxAdjustIntervalDays`（⛔ 不是"上次评估"时刻）。
  if (input.lastAdjustedAtIso !== null) {
    const last = Date.parse(input.lastAdjustedAtIso);
    const now = Date.parse(input.nowIso);
    if (Number.isFinite(last) && Number.isFinite(now)) {
      const elapsedDays = (now - last) / 86_400_000;
      if (elapsedDays < template.maxAdjustIntervalDays) {
        // ⭐ 2026-10-15 用户裁定：「没到间隔不是什么都不做，而是**计划做多少、什么时候做**」。
        //    这里**只补信息**（计划值/系数/档位 id），⛔ 不改行为：仍然是 hold、执行趟仍然不写 ✓。
        //    ⚠️ 档位本来就不动（hold 档 / 无实质变化）⇒ **不给计划值**（给了会误导成"将会调整" ✗）。
        const plannedTier = roasTierOf(ratioPpm, template.tiers);
        const planned = clampedTargetOf(plannedTier);
        if (planned === null || planned.cents === input.currentBudgetCents) {
          return hold("interval_not_elapsed", ratioPpm);
        }
        return {
          outcome: "hold",
          skipReason: "interval_not_elapsed",
          ratioPpm,
          plannedNewBudgetCents: planned.cents,
          plannedFactorPpm: planned.factorPpm,
          plannedTierId: plannedTier.id,
        };
      }
    }
  }

  // ⑥ 取档（下界闭、上界开）；`hold` 档（如 100–115%）⇒ 不动作（**类型上不带金额**）。
  const tier = roasTierOf(ratioPpm, template.tiers);
  const target = clampedTargetOf(tier);
  if (target === null) return hold("hold_band", ratioPpm);

  const clampedTarget = target.cents;
  const clamped = target.clamped;
  const direction: AutoBudgetDirection = clampedTarget >= input.currentBudgetCents ? "increase" : "decrease";

  // ⑦ 无实质变化 ⇒ 不动作（**防"假变化"**）；边界处给可读原因。
  if (clampedTarget === input.currentBudgetCents) {
    if (input.currentBudgetCents <= floor) return hold("already_at_min", ratioPpm);
    if (input.currentBudgetCents >= template.budgetMaxCents) return hold("already_at_max", ratioPpm);
    return hold(null, ratioPpm);
  }
  if (input.currentBudgetCents <= floor && direction === "decrease") return hold("already_at_min", ratioPpm);
  if (input.currentBudgetCents >= template.budgetMaxCents && direction === "increase") {
    return hold("already_at_max", ratioPpm);
  }

  return { outcome: direction, newBudgetCents: clampedTarget, ratioPpm, tierId: tier.id, clamped };
}

// ---------------------------------------------------------------------------
// 预演行 / 汇总（一期 `GET /v1/auto-budget/preview` 的响应形状）
// ---------------------------------------------------------------------------

export type AutoBudgetBudgetLevel = "campaign" | "adset";

export interface AutoBudgetPreviewRow {
  readonly shopId: string;
  readonly accountId: string;
  readonly level: AutoBudgetBudgetLevel;
  readonly campaignId: string;
  readonly adsetId: string | null;
  readonly entityName: string | null;
  readonly currency: string;
  readonly currentBudgetCents: number;
  readonly spend30dCents: number;
  readonly layerRoas7d: Roas7d | null;
  /** ⭐ **店铺 ROAS 7 天**（分母；与 `layerRoas7d` 同归因口径、同窗口）。 */
  readonly shopRoas7d: Roas7d | null;
  readonly ratioPpm: Ppm | null;
  readonly decision: AutoBudgetDecision["outcome"];
  /** **只有会动作的行才有**（跳过行原样输出当前预算，绝不 clamp）。 */
  readonly newBudgetCents: number | null;
  readonly skipReason: AutoBudgetSkipReason | null;
  readonly lastAdjustedAtIso: string | null;
  /**
   * ⭐⭐ 2026-10-15 用户裁定：「自动预算，**不要写没有到间隔时间**，应该写**计划调整的金额是多少**，
   * 然后**自动爬坡要乘以多少**。例如：当前预算 1200 × 1.1（自动预算）× 1.1（自动爬坡）= XXXX，
   * **下次调整时间是 XXXX**」。
   *
   * 语义（**只算不写**）：没到间隔时，"若间隔已过、本次会写成多少"（见 `AutoBudgetHoldDecision.plannedNewBudgetCents`）。
   * `null` = 没有计划（档位本来就不动 / 不是 `interval_not_elapsed`）⇒ 面板显示 `—`（⛔ 不是 0）。
   */
  readonly plannedNewBudgetCents?: number | null | undefined;
  /** 计划用的档位系数（ppm）；`fixed` 档 / 无计划 ⇒ `null`。面板据此显示「× 1.10」。 */
  readonly plannedFactorPpm?: Ppm | null | undefined;
  /** 计划用的档位 id（与真动作同源，便于排障）；无计划 ⇒ `null`。 */
  readonly plannedTierId?: string | null | undefined;
  /**
   * ⭐ **下次可调整时刻**（ISO；**后端算好**：`lastAdjustedAtIso + 该行生效模板的 maxAdjustIntervalDays`）。
   * `null` = 算不出来（从未调整过 / 无模板）⇒ 面板显示「可调整」（⛔ 前端不许自己算时间）。
   */
  readonly nextAdjustAtIso?: string | null | undefined;
}

export interface AutoBudgetPreviewSummary {
  readonly evaluatedLayers: number;
  readonly willAdjust: number;
  readonly totalBeforeCents: number;
  /** **只累计会动作的行**：跳过行原样、不参与加减（§6 假变化防复发）。 */
  readonly totalAfterCents: number;
  readonly deltaPpm: Ppm | null;
  readonly skippedByReason: Readonly<Partial<Record<AutoBudgetSkipReason, number>>>;
  readonly unboundCampaigns: number;
}

/** 汇总（纯函数）：**不变的行必须逐字原样**，只有 `outcome !== "hold"` 的行才进总量。 */
export function summarizeAutoBudgetPreview(input: {
  readonly rows: readonly AutoBudgetPreviewRow[];
  readonly unboundCampaigns?: number;
}): AutoBudgetPreviewSummary {
  let willAdjust = 0;
  let totalBefore = 0;
  let totalAfter = 0;
  const skippedByReason: Partial<Record<AutoBudgetSkipReason, number>> = {};
  for (const row of input.rows) {
    if (row.decision === "hold") {
      if (row.skipReason !== null) skippedByReason[row.skipReason] = (skippedByReason[row.skipReason] ?? 0) + 1;
      continue; // ⚠️ 跳过行**不进总量**（§6 bug 的根因就是这里把它算进去）
    }
    willAdjust += 1;
    totalBefore += row.currentBudgetCents;
    totalAfter += row.newBudgetCents ?? row.currentBudgetCents;
  }
  return {
    evaluatedLayers: input.rows.length,
    willAdjust,
    totalBeforeCents: totalBefore,
    totalAfterCents: totalAfter,
    deltaPpm: totalBefore === 0 ? null : Math.round(((totalAfter - totalBefore) / totalBefore) * 1_000_000),
    skippedByReason,
    unboundCampaigns: input.unboundCampaigns ?? 0,
  };
}

/** 预算变更来源（§4.5：流水必须能区分本系统调整 vs 外部调整 vs 回滚写回）。 */
export type AutoBudgetChangeSource = "auto_budget" | "external" | "rollback";

// ─────────────────────────────────────────────────────────────────────────────
// 目标 ROAS 系数爬坡（影子优先）· 2026-10-11 用户裁定
//
// 用户原话：「我暂时不想换掉 对比店铺7天ROAS的方式，因为这个是近期的指示。并且每个广告系列，
// 受到模板的约束，可能有些 ROAS 虽然很高，但是被困住了。会要求在有空间的系列，加到更高的预算
// （但要分步骤来，不能一步到位）。所以能不能再设置一个 30 天 Meta ROAS 的目标值，做为系数来逐步控制预算呢？」
//
// ⇒ 本函数**不替换** `evaluateAutoBudgetDecision`（对比店铺 7 天 ROAS 那套照旧），
//   而是在它之上叠一层**慢速牵引**：把"近期够格、长期不差、还有空间"的系列，每 3 天推一小步。
//   判定顺序与全部参数见 `docs/plan/target-roas-ramp-design-2026-10-11.md`。
// ─────────────────────────────────────────────────────────────────────────────

/** 为什么这一轮**不动**（可解释性；每个原因都能在面板上显示出来）。 */
export type TargetRampHoldReason =
  | "no_target" // 店铺没设目标 ROAS
  | "roas_missing" // 7 天 ROAS 缺失 / ≤0（⛔ 不折 0）
  | "below_entry" // 7 天 ROAS < 目标 × entryPermille（近期不够格）
  | "long_term_weak" // 30 天 ROAS < 目标 × longTermPermille（防"刚反弹一天就加"）
  | "spend_below_min_30d" // 30 天花费不足（数据不够）
  | "template_opt_out" // 模板步长 = 0（该类型退出爬坡）
  | "interval_not_elapsed" // 距上次调整不足 intervalDays
  | "at_budget_cap" // 已到模板预算上限（用户裁定：**不提醒**）
  | "already_at_target" // 已进死区（达标 ⇒ 停手）
  | "ramp_cap_30d" // 近 30 天累计爬坡已达上限
  | "quota_exhausted" // 本轮名额 / 全店增量额度用完
  | "ramp_paused" // 连续失败后被冻结（30 天冻结期内）
  | "too_many_failures" // 连续"加了就跌破"达阈值 ⇒ 本次不动（调用方同时落 30 天冻结）
  | "rule_downtrend"; // 现规则（自动预算）判定该层**应降预算** ⇒ 爬坡**不反向加价**（用户裁定 2026-10-12）

/** 这一轮**动作**的方向。`retreat` = 立即撤回最近一步爬坡（用户裁定「马上回撤」）。 */
export type TargetRampOutcome = "increase" | "retreat";

export interface TargetRampHoldDecision {
  readonly outcome: "hold";
  readonly reason: TargetRampHoldReason;
}

export interface TargetRampIncreaseDecision {
  readonly outcome: "increase";
  /** 新日预算（整数分；已夹到 [budgetMinCents, budgetMaxCents]）。 */
  readonly newBudgetCents: Cents;
  /** 本步幅度（ppm；正值）。 */
  readonly stepPpm: Ppm;
  /** 精度：`true` ⇒ 结果被预算上下限夹过。 */
  readonly clamped: boolean;
}

export interface TargetRampRetreatDecision {
  readonly outcome: "retreat";
  /** 撤回后的日预算（整数分）。撤回量 = 该系列**最近一步爬坡**的实际增量。 */
  readonly newBudgetCents: Cents;
  /** 触发回撤的 7 天 ROAS（供面板解释"为什么撤"）。 */
  readonly roas7d: number;
}

export type TargetRampDecision = TargetRampHoldDecision | TargetRampIncreaseDecision | TargetRampRetreatDecision;

export interface TargetRampThresholds {
  /** 7 天 ROAS 够格线（相对目标，ppm；默认 1_050_000 = ×1.05）。 */
  readonly entryPermille: Ppm;
  /** 30 天站得住线（相对目标，ppm；默认 950_000 = ×0.95）。 */
  readonly longTermPermille: Ppm;
  /** 7 天立即回撤线（相对目标，ppm；默认 950_000）。 */
  readonly retreatPermille: Ppm;
  /** 死区（相对目标，ppm；默认 50_000 = ±5%）。 */
  readonly deadbandPermille: Ppm;
  /** 单系列近 30 天累计爬坡上限（ppm；默认 300_000 = +30%）。 */
  readonly rampCap30dPermille: Ppm;
  /**
   * 同一系列的调整间隔（天；契约默认 3）。
   *
   * ⭐ 2026-10-12 用户裁定：「爬坡设置必须的时间间隔，**直接跟自动预算的节奏**」⇒
   * **爬坡的两个调用方（影子趟与执行趟）都传 `0`** ✓ ⇒ 不再有独立间隔 ✓，
   * 唯一的时间性约束变成「降优先」（本轮现规则动过的层让路 ✓，由调用方判 ✓）。
   * 实测动机：现规则几乎每天都调同一批层，而模板间隔常见 1 天 ⇒ 爬坡窗口薄到没有 ✗✗
   * （08:52 那轮：参与 301 / 会加 0 / 让路现规则 77，诊断逐行 `interval_not_elapsed` ✗）。
   * ⛔ 但**不可达判定**里"加完够久才评判"用的仍是**模板间隔**（调用方另算 ✓）——⛔ 不许一起归零 ✗，
   * 否则会「刚加完就判不可达并冻结」✗✗。
   */
  readonly intervalDays: number;
  /** 回撤后的冷却（天；默认 3）。⚠️ 爬坡**保留**它 = 模板间隔 ✓（防「加了就撤、撤了再加」的抖动 ✗）。 */
  readonly retreatCooldownDays: number;
  /** 连续"加了就跌破"达到几次 ⇒ 退出爬坡（默认 2）。 */
  readonly strikesToExit: number;
  /** 退出爬坡的冻结天数（默认 30）——由**调用方**据此写 `pausedUntil`。 */
  readonly failurePauseDays: number;
}

export const DEFAULT_TARGET_RAMP_THRESHOLDS: TargetRampThresholds = {
  entryPermille: 1_050_000,
  longTermPermille: 950_000,
  retreatPermille: 950_000,
  deadbandPermille: 50_000,
  // ⭐ 2026-10-11 用户裁定：单层近 30 天累计增幅上限 +30% → **+50%**。
  rampCap30dPermille: 500_000,
  intervalDays: 3,
  retreatCooldownDays: 3,
  strikesToExit: 2,
  failurePauseDays: 30,
};

export interface EvaluateTargetRampInput {
  /** 店铺级目标 ROAS（缺失 ⇒ 不参与）。 */
  readonly targetRoas: number | null;
  readonly roas7d: number | null;
  readonly roas30d: number | null;
  readonly spend30dCents: Cents;
  /** 模板：步长 0 = 退出；`minSpend30dCents` = 数据门槛。 */
  readonly template: {
    readonly targetStepPpm: Ppm;
    readonly minSpend30dCents: Cents;
    readonly budgetMinCents: Cents;
    readonly budgetMaxCents: Cents;
  };
  readonly currentBudgetCents: Cents;
  /** 距上次调整的天数（`null` = 从未调整过 ⇒ 视为已过间隔）。 */
  readonly daysSinceLastAdjust: number | null;
  /** 最近一步爬坡带来的**实际增量**（分；`null` = 没有可撤回的步）。 */
  readonly lastRampDeltaCents: Cents | null;
  /** 上一次动作是回撤 ⇒ 需要冷却（否则会"撤了立刻又加"）。 */
  readonly lastActionWasRetreat: boolean;
  /** 近 30 天累计爬坡增幅（ppm；用于上限判定）。 */
  readonly rampGain30dPermille: Ppm;
  /** 本轮剩余名额（≤0 ⇒ 不动作）；由调用方按 7 天 ROAS 降序排队后递减。 */
  readonly remainingQuota: number;
  /** 全店本轮剩余新增额度（分；≤0 ⇒ 不动作）。 */
  readonly remainingShopIncrementCents: Cents;
  /**
   * **现规则（自动预算）对同一层的方向**（用户裁定 2026-10-12：「当距离目标本来应该下降预算的时候，
   * 爬坡至少要做到不额外再加预算」）。
   *
   * · `"decrease"` ⇒ 爬坡**不加**（新原因 `rule_downtrend`）；
   * · `"increase"` / `"hold"` / `null` / `undefined`（= 该层本轮不在规则判定范围内，**无方向**）⇒ 按原逻辑；
   * · ⛔ 本字段**只挡"加"**：爬坡仍然**不下调**（那是现规则的职责 ✓），`retreat`（撤回自己加过的）也不受影响 ✓；
   * · ⚠️ 调用方必须用**现规则的同一份计算**产出它（`computeAutoBudgetPlan` 的 `decision` ✓），
   *   ⛔ 不许在爬坡侧重写一份"相对档位"逻辑。
   */
  readonly ruleDirection?: "increase" | "decrease" | "hold" | null | undefined;
  readonly thresholds?: TargetRampThresholds | undefined;
  /** 连续"加了就跌破"的次数（缺省 0）。调用方从历史里数，契约只做判定。 */
  readonly consecutiveFailures?: number | undefined;
  /** 冻结到什么时候（ISO；缺省 null）。达到 `strikesToExit` 后由调用方写入 now + failurePauseDays。 */
  readonly pausedUntilIso?: string | null | undefined;
  /** 当前时刻（ISO；缺省 ⇒ 不做冻结期判定）。 */
  readonly nowIso?: string | undefined;
}

/**
 * 纯函数：**目标系数爬坡**的一轮判定（影子模式与真实执行共用同一份逻辑 ⇒ 显示与执行同源）。
 *
 * ⚠️ 影子模式下调用方**只记录不写入**；本函数本身没有任何副作用。
 */
export function evaluateTargetRampDecision(input: EvaluateTargetRampInput): TargetRampDecision {
  const thresholds = input.thresholds ?? DEFAULT_TARGET_RAMP_THRESHOLDS;
  const hold = (reason: TargetRampHoldReason): TargetRampHoldDecision => ({ outcome: "hold", reason });

  // ① 目标缺失 ⇒ 不参与（绝不猜一个目标）
  const target = input.targetRoas;
  if (target === null || !Number.isFinite(target) || target <= 0) return hold("no_target");

  // ② 7 天 ROAS 缺失 ⇒ 不动作（⛔ 不折 0）
  const roas7d = input.roas7d;
  if (roas7d === null || !Number.isFinite(roas7d) || roas7d <= 0) return hold("roas_missing");

  // ③ 立即回撤（用户裁定「马上回撤」）：7 天 ROAS 跌破目标 × retreatPermille
  const retreatLine = (target * thresholds.retreatPermille) / 1_000_000;
  if (roas7d < retreatLine) {
    const delta = input.lastRampDeltaCents;
    if (delta === null || delta <= 0) return hold("below_entry"); // 没有可撤回的步 ⇒ 交给现规则去降
    const floor = input.template.budgetMinCents;
    const newBudget = Math.max(input.currentBudgetCents - delta, floor);
    if (newBudget >= input.currentBudgetCents) return hold("below_entry");
    return { outcome: "retreat", newBudgetCents: newBudget, roas7d };
  }

  // ③.5 冻结 / 连续失败（用户裁定：连续 2 次"加了就跌破" ⇒ 退出爬坡 30 天）
  //     ⚠️ 必须在**回撤分支之后**：冻结只禁止"继续加"，不禁止"把上一步撤回来"。
  const pausedUntil = input.pausedUntilIso ?? null;
  const nowIso = input.nowIso;
  if (pausedUntil !== null && nowIso !== undefined) {
    const pausedUntilMs = Date.parse(pausedUntil);
    const nowMs = Date.parse(nowIso);
    if (Number.isFinite(pausedUntilMs) && Number.isFinite(nowMs) && nowMs < pausedUntilMs) return hold("ramp_paused");
  }
  const failures = input.consecutiveFailures ?? 0;
  if (failures >= thresholds.strikesToExit) return hold("too_many_failures");

  // ④ 够格线：7 天 ROAS 必须 ≥ 目标 × entryPermille
  const entryLine = (target * thresholds.entryPermille) / 1_000_000;
  if (roas7d < entryLine) {
    // 已达标（进死区）⇒ 用"停手"这个更可读的原因，而不是"不够格"
    const deadband = (target * thresholds.deadbandPermille) / 1_000_000;
    return hold(Math.abs(roas7d - target) <= deadband ? "already_at_target" : "below_entry");
  }

  // ⑤ 30 天站得住线（防"刚反弹一天就加"）
  const roas30d = input.roas30d;
  if (roas30d === null || !Number.isFinite(roas30d) || roas30d <= 0) return hold("long_term_weak");
  const longTermLine = (target * thresholds.longTermPermille) / 1_000_000;
  if (roas30d < longTermLine) return hold("long_term_weak");

  // ⑥ 数据门槛
  if (input.spend30dCents < input.template.minSpend30dCents) return hold("spend_below_min_30d");

  // ⑦ 模板步长为 0 ⇒ 该类型退出爬坡（用户用它表达"这类广告要更保守"）
  const stepPpm = input.template.targetStepPpm;
  if (!Number.isInteger(stepPpm) || stepPpm <= 0) return hold("template_opt_out");

  // ⑧ 间隔（回撤后另有冷却，避免"撤了立刻又加"）
  const interval = input.lastActionWasRetreat ? thresholds.retreatCooldownDays : thresholds.intervalDays;
  if (input.daysSinceLastAdjust !== null && input.daysSinceLastAdjust < interval) {
    return hold("interval_not_elapsed");
  }

  // ⑨ 预算上限（用户裁定：**不提醒**，静默不动）
  if (input.currentBudgetCents >= input.template.budgetMaxCents) return hold("at_budget_cap");

  // ⑩ 近 30 天累计爬坡上限
  if (input.rampGain30dPermille >= thresholds.rampCap30dPermille) return hold("ramp_cap_30d");

  // ⑪ 名额 / 全店增量额度（同时性风险：10 个系列一起加 = 总花费整体抬升）
  const rawTarget = Math.round((input.currentBudgetCents * (1_000_000 + stepPpm)) / 1_000_000);
  const clampedTarget = Math.min(rawTarget, input.template.budgetMaxCents);
  const delta = clampedTarget - input.currentBudgetCents;
  if (delta <= 0) return hold("at_budget_cap");
  if (input.remainingQuota <= 0 || input.remainingShopIncrementCents < delta) return hold("quota_exhausted");

  // ⑫ 现规则方向朝下 ⇒ **爬坡不反向加价**（用户裁定 2026-10-12）
  //    理由：规则 08:30 把某层降了，爬坡 14:30 又加回来 ⇒ 净效果"降了又加"✗（跨轮没有互斥）。
  //    ⚠️ 只挡"加"：爬坡不下调（规则职责 ✓）、`retreat` 不受影响 ✓；无方向（null）不挡 ✓。
  if (input.ruleDirection === "decrease") return hold("rule_downtrend");

  return {
    outcome: "increase",
    newBudgetCents: clampedTarget,
    stepPpm,
    clamped: clampedTarget !== rawTarget,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ⭐⭐ 目标爬坡「按模板可调步长」（2026-10-11 用户裁定：「单步 10% 要在面板上能调」）
//
// 优先级（单一来源 ✓）：**模板 `config.targetStepPpm` > 环境变量 `AMS_TARGET_RAMP_STEP_PPM` > 默认 10%**
//  · 非法值（非整数 / 超范围 1%–50%）⇒ **回落下一优先级**（⛔ 不抛异常、不静默变 0 ✗）；
//  · `0` 是**合法且有意义**的（= 该模板退出爬坡 ✓，历史口径，保留 ✗ 不当非法处理）；
//  · ⛔ 本函数是纯函数（零依赖）⇒ `packages/store` 也可以调用它（那里⛔不许读 env ✓，
//    但"把 env 的值当参数传进来"是允许的 ✓）。
// ─────────────────────────────────────────────────────────────────────────────

/** 默认单步幅度（ppm）：10%。 */
export const TARGET_RAMP_STEP_PPM_DEFAULT = 100_000;
/** 单步幅度下限（ppm）：1%（低于它没有实质效果 ⇒ 视为非法）。 */
export const TARGET_RAMP_STEP_PPM_MIN = 10_000;
/** 单步幅度上限（ppm）：50%（防手滑填成 500% 之类）。 */
export const TARGET_RAMP_STEP_PPM_MAX = 500_000;

/** 单个候选值 → 合法 ppm；非法 ⇒ `null`（`0` 合法 = 退出爬坡）。 */
export function normalizeTargetRampStepPpm(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isSafeInteger(raw) || raw < 0) return null;
  if (raw === 0) return 0;
  if (raw < TARGET_RAMP_STEP_PPM_MIN || raw > TARGET_RAMP_STEP_PPM_MAX) return null;
  return raw;
}

/**
 * 解析**生效**的单步幅度（ppm）：模板 > env > 默认 10%。
 * @param input.templateValue 该层所属模板的 `config.targetStepPpm`（缺省/非法 ⇒ 看 env）
 * @param input.envValue 应用层读到的 `AMS_TARGET_RAMP_STEP_PPM`（字符串或数字；缺省/非法 ⇒ 默认）
 */
export function resolveTargetRampStepPpm(input: {
  readonly templateValue?: number | null | undefined;
  readonly envValue?: string | number | null | undefined;
}): number {
  const fromTemplate = normalizeTargetRampStepPpm(input.templateValue ?? null);
  if (fromTemplate !== null) return fromTemplate;

  const rawEnv = input.envValue;
  const envNumber =
    typeof rawEnv === "string"
      ? rawEnv.trim() === ""
        ? Number.NaN
        : Number(rawEnv.trim())
      : typeof rawEnv === "number"
        ? rawEnv
        : Number.NaN;
  const fromEnv = normalizeTargetRampStepPpm(envNumber);
  if (fromEnv !== null) return fromEnv;

  return TARGET_RAMP_STEP_PPM_DEFAULT;
}

// ─────────────────────────────────────────────────────────────────────────────
// 目标爬坡 = 自动预算的「叠加层」（用户裁定 2026-10-13）· 第 1 块：合并纯函数
//
//   用户原话：「爬坡仅仅是作用在自动预算上边的调整，**它自己不会操作调整**」
//   ⇒ 全系统**只有一条写路径**（自动预算 ✓），爬坡只提供"叠加量" ✓。
//
//   finalCents = clamp(baseCents + overlayCents, templateMin, templateMax)
//     · baseCents    = 自动预算档位给出的新预算（唯一写者）
//     · overlayCents = round(baseCents × stepPpm)（步长优先级见 resolveTargetRampStepPpm）
//     · 夹取顺序：模板 MIN/MAX ⇒ 名额 ⇒ 全店单轮新增 ≤10% ⇒ 单层 30 天累计 ≤+50%
//     · 自动预算判「降」⇒ overlay = 0（沿用既有方向门口径 rule_downtrend）
//     · 爬坡**永不**把最终值压到 baseCents 之下（降预算始终是自动预算的职责）
//
//   ⛔ 本函数是**纯函数**（零依赖、不读 env、不写库）⇒ app/store 都可调用；
//      "名额/全店额度/30 天累计"这些**跨层状态由调用方算好后当参数传进来** ✓。
//   ⛔ 缺失 ≠ 0：拿不到 base 或模板上下限 ⇒ 明确返回"无法合并"的原因，⛔ 不猜、不折 0。
// ─────────────────────────────────────────────────────────────────────────────

/** 叠加合并的结果：为什么加/不加、加多少、最终值。 */
export type TargetRampOverlayReason =
  | "overlay_applied" // 正常叠加
  | "overlay_disabled" // 回退开关关掉了叠加（行为 = 纯自动预算）
  | "rule_downtrend" // 自动预算判该层应降 ⇒ 不叠加（沿用既有方向门口径）
  | "template_opt_out" // 模板步长 = 0（该模板退出爬坡）
  | "base_missing" // 拿不到自动预算的 base（缺失 ≠ 0）
  | "bounds_missing" // 拿不到模板预算上下限 ⇒ 无法夹取（⛔ 不猜）
  | "at_budget_cap" // base 已 ≥ 模板上限 ⇒ 加不动
  | "overlay_zero" // 算出来的叠加量本身为 0（如 base = 0）
  | "quota_exhausted" // 每轮每店名额已用完
  | "shop_increment_exhausted" // 全店单轮新增额度已用完
  | "ramp_cap_30d"; // 单层近 30 天累计已达上限

export interface ResolveTargetRampOverlayInput {
  /** 自动预算档位给出的新预算（整数分）。缺失 ⇒ `base_missing`（⛔ 不折 0）。 */
  readonly baseCents: number | null | undefined;
  /** 模板预算下限（整数分）。与上限同为缺失 ⇒ `bounds_missing`（⛔ 不猜）。 */
  readonly templateMinCents: number | null | undefined;
  /** 模板预算上限（整数分）。 */
  readonly templateMaxCents: number | null | undefined;
  /** 该层所属模板的 `config.targetStepPpm`（缺省/非法 ⇒ 看 env ⇒ 默认 10%）。 */
  readonly templateStepPpm?: number | null | undefined;
  /** 应用层读到的 `AMS_TARGET_RAMP_STEP_PPM`（⛔ 本函数不读 env，由调用方传值）。 */
  readonly envStepPpm?: string | number | null | undefined;
  /** 自动预算对该层的方向（`"decrease"` ⇒ 不叠加）。`null`/缺省 = 拿不到方向 ⇒ **不挡**。 */
  readonly ruleDirection?: "increase" | "decrease" | "hold" | null | undefined;
  /** 每轮每店名额是否还有（调用方按名额判定）；`false` ⇒ `quota_exhausted`。 */
  readonly quotaAvailable?: boolean | undefined;
  /** 全店单轮新增额度的**剩余**（整数分；调用方算）。缺省 = 不限制。 */
  readonly remainingShopIncrementCents?: number | null | undefined;
  /** 单层近 30 天累计的**剩余**空间（整数分；调用方算）。缺省 = 不限制。 */
  readonly remaining30dCapCents?: number | null | undefined;
  /** 回退开关（`AMS_TARGET_RAMP_OVERLAY` 等价）；`false` ⇒ overlay ≡ 0。缺省 = 开。 */
  readonly overlayEnabled?: boolean | undefined;
}

export interface TargetRampOverlayResult {
  readonly outcome: "overlay" | "none";
  readonly reason: TargetRampOverlayReason;
  /** **生效**的叠加量（整数分；≥0）。`none` ⇒ 0。 */
  readonly overlayCents: Cents;
  /** 夹取**之前**想要的叠加量（整数分；审计用，便于解释"为什么只加了这么点"）。 */
  readonly requestedOverlayCents: Cents;
  /** 合并后的最终预算（整数分）。`none` ⇒ 原样返回 base（拿不到 base ⇒ `null`）。 */
  readonly finalCents: Cents | null;
  /** 生效步长（ppm；含优先级解析结果，便于审计/面板显示）。 */
  readonly stepPpm: Ppm;
  /** 是否被模板上下限夹过。 */
  readonly clampedByBounds: boolean;
  /** 是否被"全店额度 / 30 天累计"压小过。 */
  readonly clampedByAllowance: boolean;
}

/** 把可能缺失的整数分规整成"非负安全整数"；非法 ⇒ `null`（缺失 ≠ 0）。 */
function safeCents(raw: number | null | undefined): number | null {
  if (typeof raw !== "number" || !Number.isSafeInteger(raw) || raw < 0) return null;
  return raw;
}

/**
 * 合并"自动预算基准 + 爬坡叠加"（纯函数；⛔ 不写任何东西）。
 *
 * ⚠️ 语义要点（用户裁定 + 设计定稿）：
 *   · 只**加**不**减** ⇒ 结果永不小于 `baseCents` ✓（降预算由自动预算负责 ✓）；
 *   · 自动预算判「降」⇒ 不叠加 ✓；**拿不到方向 ⇒ 不挡** ✓；
 *   · 缺 base / 缺模板上下限 ⇒ 明确返回原因 ✓（⛔ 不猜、不折 0）。
 */
export function resolveTargetRampOverlay(input: ResolveTargetRampOverlayInput): TargetRampOverlayResult {
  const stepPpm = resolveTargetRampStepPpm({
    templateValue: input.templateStepPpm ?? null,
    envValue: input.envStepPpm ?? null,
  });
  const base = safeCents(input.baseCents);

  const none = (reason: TargetRampOverlayReason): TargetRampOverlayResult => ({
    outcome: "none",
    reason,
    overlayCents: 0,
    requestedOverlayCents: 0,
    finalCents: base,
    stepPpm,
    clampedByBounds: false,
    clampedByAllowance: false,
  });

  if (input.overlayEnabled === false) return none("overlay_disabled");
  if (base === null) return none("base_missing");

  const min = safeCents(input.templateMinCents);
  const max = safeCents(input.templateMaxCents);
  if (min === null || max === null || max < min) return none("bounds_missing");

  if (input.quotaAvailable === false) return none("quota_exhausted");
  if (input.ruleDirection === "decrease") return none("rule_downtrend");
  if (stepPpm === 0) return none("template_opt_out");
  if (base >= max) return none("at_budget_cap");
  if (base <= 0) return none("overlay_zero");

  const requested = Math.round((base * stepPpm) / 1_000_000);
  if (requested <= 0) return none("overlay_zero");

  let overlay = requested;
  let clampedByAllowance = false;
  let allowanceReason: TargetRampOverlayReason | null = null;
  for (const [rawRemaining, reason] of [
    [input.remainingShopIncrementCents, "shop_increment_exhausted"],
    [input.remaining30dCapCents, "ramp_cap_30d"],
  ] as const) {
    if (rawRemaining === null || rawRemaining === undefined) continue; // 不限制
    const remaining = Math.max(0, Math.trunc(rawRemaining));
    if (overlay > remaining) {
      overlay = remaining;
      clampedByAllowance = true;
      allowanceReason = reason;
    }
  }
  if (overlay <= 0) return none(allowanceReason ?? "overlay_zero");

  const rawFinal = base + overlay;
  const finalCents = rawFinal > max ? max : rawFinal;
  const clampedByBounds = finalCents !== rawFinal;

  return {
    outcome: "overlay",
    reason: "overlay_applied",
    overlayCents: finalCents - base, // 夹取后**真正生效**的增量
    requestedOverlayCents: requested,
    finalCents,
    stepPpm,
    clampedByBounds,
    clampedByAllowance,
  };
}

